<template>
  <div :class="componentClass.root">
    <PmFormPure @submit.prevent="submit">
      <PmCheckboxPure
        v-model:value="sendCopyToMe"
        label="Kopie an mich"
        note="Bei Anfragen, Nachrichten oder anderer Kommunikation mit angefragten Ressourcen eine Kopie an mich senden"
      />

      <template #status>
        <PmErrorNotificationPure
          v-if="state.meta.value?.error === true"
          :error="props.error"
          :message="state.meta.value?.errorMessage"
        />
      </template>

      <template #control>
        <PmButtonPure
          icon="save"
          label="Speichern"
          variant="primary"
          class="PmDefaultViewsPure-submit"
          :loading="isLoading"
          type="submit"
          :disabled="!hasFormChanges"
        />
      </template>
    </PmFormPure>
  </div>
</template>

<script setup lang="ts">
import { toRef, computed } from 'vue'
import { useComponentClass } from '@thomasaull-shared/composables'
import { useXState, type StatePathOrValue } from '@/composition/useXState5'
import { PmSettingsRequestPureState } from './PmSettingsRequestPureState'
import PmCheckboxPure from '@/components/basics/PmCheckboxPure.vue'
import PmFormPure from '../basics/PmForm/PmFormPure.vue'
import PmErrorNotificationPure from '../basics/PmErrorNotificationPure.vue'
import type { FriendlyError } from '@/functional/friendlyErrors'
import PmButtonPure from '../basics/PmButtonPure.vue'
import { isEqual } from 'lodash-es'

export type Props = {
  state?: StatePathOrValue<typeof PmSettingsRequestPureState>
  error?: FriendlyError
  savedFormData?: Partial<FormData>
}

const props = withDefaults(defineProps<Props>(), {})

type FormData = {
  sendCopyToMe: boolean
}

export type Models = {
  formData: FormData
}

const formData = defineModel<Models['formData']>('formData', {
  default: {
    sendCopyToMe: true,
  },
})

const sendCopyToMe = computed({
  get: () => formData.value.sendCopyToMe,
  set: (value) => (formData.value = { ...formData.value, sendCopyToMe: value }),
})

export type Emits = {
  submit: [FormData]
}

const emit = defineEmits<Emits>()

const state = useXState(PmSettingsRequestPureState, {
  syncStateWith: toRef(props, 'state'),
})

const componentClass = useComponentClass()

const hasFormChanges = computed(() => {
  return !isEqual(formData.value, props.savedFormData)
})

const isLoading = computed(() => {
  if (state.value.snapshot.matches({ save: 'saving' })) return true

  return false
})

function submit() {
  emit('submit', formData.value)
}
</script>

<style lang="scss">
.PmSettingsRequestPure {
  $block: &;
}
</style>
